
import { defineComponent, ref, inject, watchEffect, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { Apollo, Notify } from '@/core/services';
import { GET_PRODUCT_GROUP, SEARCH_PRODUCT_GROUPS } from '@/modules/catalog/product-groups/graphql/Queries';
import { DELETE_PRODUCT_GROUP } from '@/modules/catalog/product-groups/graphql/Mutations';
import Search from '@/components/search/Search.vue';
import { useI18n } from 'vue-i18n';
import Table from '../../../../components/Table/Table.vue';
import InnerLoader from '../../../../components/InnerLoader.vue';
import { dropdown_handler } from '../../../../core/helpers/dropdownHandler';
import { useRouter } from 'vue-router';

export default defineComponent({
    name: 'customer-group-list',
    components: { Search, Table, InnerLoader },
    setup() {
        const store = useStore();
        const productGroupData: any = ref([]);
        const pagination = ref({}) as Record<any, any>;
        const clickHandler = ref() as Record<any, any>;
        const currentPage = ref();
        const emitter: any = inject('emitter');
        const loader = ref(false);
        const loading = ref(false);
        const system_locale = ref();
        const i18n = useI18n();
        const activeIndex = ref(-1);
        const searchObservable: any = ref(null);
        const observable: any = ref(null);
        const permissions = store.getters.getPermissions;
        const router = useRouter();
        const columns = ref([
            {
                label: 'message.ID',
                key: 'id'
            },
            {
                label: 'message.NAME',
                key: 'name'
            },
            {
                label: 'message.CREATED_AT',
                key: 'created_at'
            },
            {
                label: 'message.ACTIONS',
                key: 'actions',
                textAlignment: 'center'
            }
        ]);

        watchEffect(() => {
            system_locale.value = i18n.fallbackLocale.value;
        });

        const dropdownHandler = (index, id) => {
            activeIndex.value = index === id ? null : id;
        };
        const handleClickOutside = event => {
            if (!event.target.closest('.action-btn')) {
                activeIndex.value = -1;
            }
        };

        const productGroups = (page = 1, reload = false) => {
            if (searchObservable.value != null) {
                searchObservable.value?.unsubscribe();
                searchObservable.value = null;
            }
            if (page == 1) observable.value?.unsubscribe();
            loader.value = true;
            observable.value = Apollo.watchQuery({
                query: GET_PRODUCT_GROUP,
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'cache-and-network',
                errorPolicy: 'all',
                variables: {
                    page: page,
                    limit: 10
                }
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loader.value = false;
                }
                reload ? (currentPage.value = 1) : false;
                clickHandler.value = productGroups;
                productGroupData.value = data.product_groups.data;
                pagination.value = data.product_groups;
                loader.value = false;
            });
        };

        const handleCurrentChange = (num: number) => {
            clickHandler.value(num);
        };

        const searchHandler = (page = 1) => {
            if (observable.value != null) {
                observable.value?.unsubscribe();
                observable.value = null;
            }
            const searchData = store.getters.getSearchData;
            loader.value = true;
            searchObservable.value?.unsubscribe();
            searchObservable.value = Apollo.watchQuery({
                query: SEARCH_PRODUCT_GROUPS,
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'network-only',
                variables: {
                    page: page,
                    limit: 10,
                    search_key: searchData.data.input,
                    locale: system_locale.value
                }
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loader.value = false;
                }
                clickHandler.value = searchHandler;
                productGroupData.value = data.search_product_groups.data;
                pagination.value = data.search_product_groups;
                loader.value = false;
            });
        };

        const handleDelete = (id: number) => {
            store.getters.appInstance.$messageBox
                .confirm(`${i18n.t('message.ARE_YOU_SURE_TO_DELETE_RECORD')}?`, i18n.t('message.INFO'), {
                    confirmButtonText: i18n.t('message.OK'),
                    cancelButtonText: i18n.t('message.CANCEL'),
                    type: 'info'
                })
                .then(async () => {
                    loader.value = true;
                    await Apollo.mutate({
                        mutation: DELETE_PRODUCT_GROUP,
                        variables: { id: id },
                        update: (store, { data: { delete_product_group } }) => {
                            if (searchObservable.value != null) searchHandler(pagination.value.current_page);
                            else productGroups(pagination.value.current_page);
                            loader.value = false;
                        }
                    });
                    Notify.success(i18n.t('message.RECORD_DELETED_SUCCESSFULLY'));
                })
                .catch(() => {
                    loader.value = false;
                });
        };

        productGroups();

        const handleEdit = (product_group: Record<any, any>) => {
            product_group = { ...product_group, current_page: pagination.value.current_page };
            emitter.emit('editProductGroupAction', product_group);
        };

        const handleAssignment = (product_group: Record<any, any>) => {
            product_group = { ...product_group, current_page: pagination.value.current_page };
            emitter.emit('assignProductGroupAction', product_group);
        };

        const handleView = (product_group: Record<any, any>) => {
            product_group = { ...product_group, current_page: pagination.value.current_page };
            emitter.emit('viewGroupProductsAction', product_group);
        };

        onMounted(() => {
            document.addEventListener('click', handleClickOutside);
        });

        const exportFile = data => {
            const searchData = store.getters.getSearchData;
            loading.value = true;
            const sub = Apollo.watchQuery({
                query: SEARCH_PRODUCT_GROUPS,
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
                variables: {
                    page: 0,
                    limit: 1,
                    filter: searchData?.data?.select ? searchData?.data?.select : '',
                    search_key: searchData?.data?.input ? searchData?.data?.input : '',
                    locale: system_locale.value,
                    type: data?.type
                }
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loading.value = false;
                } else if (data) {
                    window.open(data?.search_product_groups?.data[0]?.path, '_blank');
                    loading.value = false;
                }
                loading.value = false;
                sub.unsubscribe();
            });
        };

        onUnmounted(() => {
            observable.value?.unsubscribe();
            searchObservable.value?.unsubscribe();
        });

        return {
            pagination,
            productGroupData,
            currentPage,
            permissions,
            columns,
            loading,
            loader,
            exportFile,
            searchHandler,
            productGroups,
            handleCurrentChange,
            handleEdit,
            handleAssignment,
            handleView,
            handleDelete,
            activeIndex,
            dropdownHandler,
            handleClickOutside
        };
    }
});
